import type { FolderImportIdentity, StagedCapture } from '../shared/desktop-api';

/**
 * How a folder added to a job that has its setup is divided into waves: CapturePoint's own policy
 * (apps/desktop/src/main/folder-wave-policy.ts), at the cadence it chooses for a job read in the
 * cloud ("admitted-cloud"): a two-file first wave puts documents in review within a minute, the rest
 * go in waves the pools can keep busy. Waves are scheduling only: every file is in exactly one wave,
 * and every count on screen is of the whole folder (renderer/folder-import-progress.ts).
 *
 * Paige's one change: a folder that fits one wave is one wave. The desktop's files were already on
 * its disk; here each wave goes up before it is read and the queue reads one wave at a time, so a
 * two-file first wave made the rest of a 100-file folder wait behind a 20 s run of its own, and the
 * host and the cards did that run's fixed work twice (docs/status/2026-10-09-engine-road-speed.md,
 * fix 6). A longer folder keeps the quick first wave: there it is a small part of the whole.
 */
export const folderWavePolicy = {
  mode: 'accelerated',
  firstWaveFiles: 2,
  subsequentWaveFiles: 128,
} as const;

export interface FolderWaveRange {
  start: number;
  end: number;
}

/**
 * A gap-free, non-overlapping partition of [0, total): CapturePoint's `folderWaveRanges`, except
 * that a folder of at most one wave's files (`subsequentWaveFiles`) is that one wave.
 */
export function folderWaveRanges(
  total: number,
  policy: { firstWaveFiles: number; subsequentWaveFiles: number } = folderWavePolicy,
): FolderWaveRange[] {
  if (!Number.isSafeInteger(total) || total < 0)
    throw new RangeError('Folder file count must be a non-negative safe integer.');
  if (total === 0) return [];
  if (total <= policy.subsequentWaveFiles) return [{ start: 0, end: total }];
  const ranges: FolderWaveRange[] = [{ start: 0, end: Math.min(total, policy.firstWaveFiles) }];
  for (let start = ranges[0]!.end; start < total; start += policy.subsequentWaveFiles)
    ranges.push({ start, end: Math.min(total, start + policy.subsequentWaveFiles) });
  return ranges;
}

/** One wave as the queue holds it, and the browser's files it is made of. */
export interface FolderWave {
  capture: StagedCapture;
  files: File[];
  /** Files of the folder before this wave: where its upload line starts counting. */
  offset: number;
}

/**
 * The staged waves of one folder, named as the desktop names them ("Invoices · wave 2 of 5", or
 * "Invoices · 2 files" when one wave holds it all), each carrying the whole folder's identity.
 */
export function folderWaves(jobId: string, files: File[], folderName: string): FolderWave[] {
  const ranges = folderWaveRanges(files.length);
  const ids = ranges.map(() => crypto.randomUUID());
  const folderImport: FolderImportIdentity = {
    id: ids[0] ?? '',
    waves: ranges.map((range, wave) => ({ id: ids[wave]!, fileCount: range.end - range.start })),
  };
  const name = folderName || 'Folder';
  const createdAt = new Date().toISOString();
  return ranges.map((range, wave) => ({
    offset: range.start,
    files: files.slice(range.start, range.end),
    capture: {
      id: ids[wave]!,
      jobId,
      sourceKind: 'files',
      sourceLabel:
        ranges.length === 1
          ? `${name} · ${(range.end - range.start).toLocaleString()} files`
          : `${name} · wave ${wave + 1} of ${ranges.length}`,
      fileCount: range.end - range.start,
      waveMode: folderWavePolicy.mode,
      folderImport,
      status: 'pending',
      createdAt,
      error: null,
      attempts: 0,
    },
  }));
}
